import { useRef } from "react";
import { Link } from "react-router-dom";
import useLiveScores from "@/hooks/useLiveScores";
import { Radio } from "lucide-react";

/**
 * ESPN-style horizontal live scores ticker.
 * Shows: ARS 4-1 C.PLC (FT), RMA 3-1 GTF (92'), CHE 2-1 MUN (56')
 * Live updates via WebSocket (/api/ws/live-scores) — falls back to HTTP polling.
 */
const LiveScoresTicker = () => {
    const { matches, loading, source, connected } = useLiveScores({ window: "today" });
    const scrollRef = useRef(null);

    if (loading && matches.length === 0) {
        return (
            <div
                className="bg-black border-y border-border h-10 flex items-center"
                data-testid="live-scores-ticker-loading"
            >
                <div className="container-main text-xs text-white/40">
                    Inapakia matokeo...
                </div>
            </div>
        );
    }

    if (matches.length === 0) {
        return null;
    }

    const hasLive = matches.some((m) => m.state === "in");
    // Duplicate items for seamless marquee loop
    const tickerItems = [...matches, ...matches];

    return (
        <div
            className="bg-black border-y border-electric/20 overflow-hidden"
            data-testid="live-scores-ticker"
        >
            <div className="flex items-center">
                {/* Label */}
                <div
                    className={`flex items-center gap-2 px-3 py-2 shrink-0 border-r border-electric/30 ${
                        hasLive ? "bg-red-600" : "bg-electric"
                    }`}
                >
                    <Radio
                        className={`h-3.5 w-3.5 ${hasLive ? "text-white animate-pulse" : "text-white"}`}
                    />
                    <span
                        className={`text-xs font-heading font-black uppercase tracking-wider ${
                            hasLive ? "text-white" : "text-white"
                        }`}
                    >
                        {hasLive ? "Live" : "Matokeo"}
                    </span>
                </div>

                {/* Marquee */}
                <div className="flex-1 overflow-hidden">
                    <div
                        ref={scrollRef}
                        className="flex items-center gap-6 whitespace-nowrap animate-ticker py-2"
                    >
                        {tickerItems.map((m, idx) => (
                            <Link
                                to="/mechi"
                                key={`${m.id}-${idx}`}
                                className="flex items-center gap-2 text-sm group"
                                data-testid={`ticker-match-${idx}`}
                            >
                                <span className="text-white/40 text-[10px] uppercase tracking-wider">
                                    {m.flag} {m.competition_name}
                                </span>
                                <span className="text-white font-semibold group-hover:text-electric transition-colors">
                                    {m.home_abbr}
                                </span>
                                <span
                                    className={`font-mono font-bold ${
                                        m.state === "in"
                                            ? "text-red-500"
                                            : m.state === "post"
                                            ? "text-electric"
                                            : "text-white/40"
                                    }`}
                                >
                                    {m.home_score ?? "-"} - {m.away_score ?? "-"}
                                </span>
                                <span className="text-white font-semibold group-hover:text-electric transition-colors">
                                    {m.away_abbr}
                                </span>
                                <span
                                    className={`text-[11px] px-1.5 py-0.5 rounded font-bold ${
                                        m.state === "in"
                                            ? "bg-red-500/20 text-red-400 animate-pulse"
                                            : m.state === "post"
                                            ? "bg-white/5 text-white/60"
                                            : "bg-electric/10 text-electric"
                                    }`}
                                >
                                    {m.minute_label}
                                </span>
                                <span className="text-white/20">•</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveScoresTicker;
